import { uuid } from '../../../lib/uuid';
import { useState, useEffect, useCallback } from 'react';
import { loadWebhooks, saveWebhooks, fireWebhook, WEBHOOK_EVENTS } from '../../../lib/webhooks';

// ─── Helpers ──────────────────────────────────────────────────────────────────
function newHook(overrides = {}) {
  return {
    id: uuid(),
    url: '',
    events: [],
    secret: '',
    active: true,
    lastFiredAt: null,
    lastStatus: null,
    ...overrides,
  };
}

// ─── Webhook row ──────────────────────────────────────────────────────────────
function WebhookRow({ hook, onUpdate, onDelete, onTest }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={`border rounded-lg transition-colors ${hook.active ? 'border-gray-200 dark:border-[#243348]' : 'border-gray-100 dark:border-[#1B2A3E] opacity-60'} bg-white dark:bg-[#1B2A3E]`}>
      {/* Summary row */}
      <div className="flex items-center gap-3 px-4 py-3">
        {/* Active toggle */}
        <button
          onClick={() => onUpdate({ ...hook, active: !hook.active })}
          title={hook.active ? 'Disable' : 'Enable'}
          className={`relative w-8 h-4.5 rounded-full transition-colors flex-shrink-0 ${hook.active ? 'bg-[#2563EB]' : 'bg-gray-200 dark:bg-[#243348]'}`}
          style={{ height: '18px', width: '32px' }}
        >
          <span
            className={`absolute top-0.5 left-0.5 w-3.5 h-3.5 rounded-full bg-white shadow transition-transform ${hook.active ? 'translate-x-[14px]' : ''}`}
          />
        </button>

        {/* URL */}
        <div className="flex-1 min-w-0">
          <p className="text-[var(--wm-text-ui)] font-medium text-[#0F1923] dark:text-[#F8FAFE] truncate font-mono">{hook.url || <span className="italic text-[#64748B]">No URL set</span>}</p>
          <p className="text-[var(--wm-text-caption)] text-[#64748B] dark:text-[#7D93AE] mt-0.5">
            {hook.events.length > 0 ? hook.events.join(', ') : 'No events selected'}
          </p>
        </div>

        {/* Last status */}
        {hook.lastStatus && (
          <span className={`text-[var(--wm-text-caption)] font-semibold px-1.5 py-0.5 rounded-full flex-shrink-0 ${hook.lastStatus === 'ok' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'}`}>
            {hook.lastStatus === 'ok' ? 'OK' : 'Failed'}
          </span>
        )}
        {hook.lastFiredAt && (
          <span className="text-[var(--wm-text-caption)] text-[#64748B] dark:text-[#7D93AE] flex-shrink-0 hidden sm:inline">
            {new Date(hook.lastFiredAt).toLocaleDateString()}
          </span>
        )}

        {/* Actions */}
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={() => onTest(hook)}
            title="Send test event"
            className="px-2 py-1 text-[var(--wm-text-caption)] font-semibold rounded-lg border border-gray-200 dark:border-[#243348] text-[#64748B] dark:text-[#7D93AE] hover:bg-gray-50 dark:hover:bg-[#243348] transition-colors"
          >
            Test
          </button>
          <button
            onClick={() => setExpanded(v => !v)}
            title="Edit"
            className="p-1.5 rounded-lg text-[#64748B] dark:text-[#7D93AE] hover:bg-gray-100 dark:hover:bg-[#243348] transition-colors"
          >
            <svg className={`w-3.5 h-3.5 transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
            </svg>
          </button>
          <button
            onClick={() => onDelete(hook.id)}
            title="Delete"
            className="p-1.5 rounded-lg text-[#64748B] dark:text-[#7D93AE] hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
            </svg>
          </button>
        </div>
      </div>

      {/* Expanded editor */}
      {expanded && (
        <div className="border-t border-gray-100 dark:border-[#243348] px-4 py-3 space-y-3">
          <div>
            <label className="block text-[var(--wm-text-ui)] font-medium text-[#64748B] dark:text-[#7D93AE] mb-1">Endpoint URL *</label>
            <input
              value={hook.url}
              onChange={e => onUpdate({ ...hook, url: e.target.value })}
              placeholder="https://your-server.example.com/webhook"
              className="wm-input w-full text-[var(--wm-text-ui)] font-mono"
            />
          </div>
          <div>
            <label className="block text-[var(--wm-text-ui)] font-medium text-[#64748B] dark:text-[#7D93AE] mb-1.5">Events</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {WEBHOOK_EVENTS.map(evt => (
                <label key={evt} className="flex items-center gap-2 text-[var(--wm-text-ui)] text-[#0F1923] dark:text-[#F8FAFE] cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={hook.events.includes(evt)}
                    onChange={e => {
                      const events = e.target.checked
                        ? [...hook.events, evt]
                        : hook.events.filter(v => v !== evt);
                      onUpdate({ ...hook, events });
                    }}
                    className="rounded border-gray-300 dark:border-[#243348] text-[#2563EB] focus:ring-[#2563EB]"
                  />
                  <span className="font-mono text-[var(--wm-text-caption)]">{evt}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-[var(--wm-text-ui)] font-medium text-[#64748B] dark:text-[#7D93AE] mb-1">
              Signing Secret <span className="font-normal">(optional — used for HMAC-SHA256)</span>
            </label>
            <input
              type="password"
              value={hook.secret || ''}
              onChange={e => onUpdate({ ...hook, secret: e.target.value })}
              placeholder="whsec_..."
              className="wm-input w-full text-[var(--wm-text-ui)] font-mono"
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Add webhook form ─────────────────────────────────────────────────────────
function AddWebhookForm({ onAdd, onCancel }) {
  const [hook, setHook] = useState(newHook());

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!hook.url.trim()) return;
    onAdd(hook);
  };

  return (
    <form onSubmit={handleSubmit} className="border border-[#2563EB]/40 rounded-lg bg-blue-50/30 dark:bg-[#1B2A3E] p-4 space-y-3">
      <h3 className="text-[var(--wm-text-ui)] font-semibold text-[#0F1923] dark:text-[#F8FAFE]">New Webhook</h3>
      <div>
        <label className="block text-[var(--wm-text-ui)] font-medium text-[#64748B] dark:text-[#7D93AE] mb-1">Endpoint URL *</label>
        <input
          required
          value={hook.url}
          onChange={e => setHook(h => ({ ...h, url: e.target.value }))}
          placeholder="https://your-server.example.com/webhook"
          className="wm-input w-full text-[var(--wm-text-ui)] font-mono"
        />
      </div>
      <div>
        <label className="block text-[var(--wm-text-ui)] font-medium text-[#64748B] dark:text-[#7D93AE] mb-1.5">Events</label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {WEBHOOK_EVENTS.map(evt => (
            <label key={evt} className="flex items-center gap-2 text-[var(--wm-text-ui)] text-[#0F1923] dark:text-[#F8FAFE] cursor-pointer select-none">
              <input
                type="checkbox"
                checked={hook.events.includes(evt)}
                onChange={e => {
                  const events = e.target.checked
                    ? [...hook.events, evt]
                    : hook.events.filter(v => v !== evt);
                  setHook(h => ({ ...h, events }));
                }}
                className="rounded border-gray-300 dark:border-[#243348] text-[#2563EB] focus:ring-[#2563EB]"
              />
              <span className="font-mono text-[var(--wm-text-caption)]">{evt}</span>
            </label>
          ))}
        </div>
      </div>
      <div>
        <label className="block text-[var(--wm-text-ui)] font-medium text-[#64748B] dark:text-[#7D93AE] mb-1">
          Signing Secret <span className="font-normal">(optional)</span>
        </label>
        <input
          type="password"
          value={hook.secret}
          onChange={e => setHook(h => ({ ...h, secret: e.target.value }))}
          placeholder="whsec_..."
          className="wm-input w-full text-[var(--wm-text-ui)] font-mono"
        />
      </div>
      <div className="flex items-center justify-end gap-2">
        <button type="button" onClick={onCancel} className="px-3 py-1.5 text-[var(--wm-text-ui)] font-medium rounded-lg border border-gray-200 dark:border-[#243348] text-[#64748B] dark:text-[#7D93AE] hover:bg-gray-50 dark:hover:bg-[#243348] transition-colors">Cancel</button>
        <button type="submit" className="px-3 py-1.5 text-[var(--wm-text-ui)] font-medium rounded-lg bg-[#2563EB] text-white hover:bg-[#1d4ed8] transition-colors">Save Webhook</button>
      </div>
    </form>
  );
}

// ─── Main tab ─────────────────────────────────────────────────────────────────
export default function WebhooksTab() {
  const [hooks, setHooks] = useState(() => loadWebhooks());
  const [showAdd, setShowAdd] = useState(false);
  const [testStatus, setTestStatus] = useState(null); // { id, status }

  // Persist on every change
  useEffect(() => { saveWebhooks(hooks); }, [hooks]);

  const handleUpdate = useCallback((updated) => {
    setHooks(prev => prev.map(h => h.id === updated.id ? updated : h));
  }, []);

  const handleDelete = useCallback((id) => {
    setHooks(prev => prev.filter(h => h.id !== id));
  }, []);

  const handleAdd = useCallback((hook) => {
    setHooks(prev => [...prev, hook]);
    setShowAdd(false);
  }, []);

  const handleTest = useCallback(async (hook) => {
    setTestStatus({ id: hook.id, status: 'firing' });
    // Update lastFiredAt optimistically
    const now = new Date().toISOString();
    setHooks(prev => prev.map(h => h.id === hook.id ? { ...h, lastFiredAt: now } : h));

    try {
      await fireWebhook('estimate.created', {
        test: true,
        estimateId: 'test-' + Date.now(),
        estimateNumber: 'WM-TEST',
        customerName: 'Test Customer',
        total: 0,
      });
      setHooks(prev => prev.map(h => h.id === hook.id ? { ...h, lastStatus: 'ok', lastFiredAt: now } : h));
      setTestStatus({ id: hook.id, status: 'ok' });
    } catch {
      setHooks(prev => prev.map(h => h.id === hook.id ? { ...h, lastStatus: 'error', lastFiredAt: now } : h));
      setTestStatus({ id: hook.id, status: 'error' });
    }
    setTimeout(() => setTestStatus(null), 3000);
  }, []);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[var(--wm-text-body)] font-semibold text-[#0F1923] dark:text-[#F8FAFE]">Outbound Webhooks</h2>
          <p className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE] mt-0.5">
            Send real-time event notifications to external endpoints when things happen in WrapMind.
          </p>
        </div>
        <button
          onClick={() => setShowAdd(v => !v)}
          className="flex items-center gap-1.5 px-2.5 py-1.5 text-[var(--wm-text-ui)] font-medium rounded-lg bg-[#2563EB] text-white hover:bg-[#1d4ed8] transition-colors flex-shrink-0"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
          </svg>
          Add Webhook
        </button>
      </div>

      {/* Dev mode notice */}
      {import.meta.env.VITE_DEV_AUTH === '1' && (
        <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/40 text-[var(--wm-text-ui)] text-amber-700 dark:text-amber-400">
          <svg className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
          <span><strong>Dev mode:</strong> webhooks will only log to console — no real HTTP requests are sent.</span>
        </div>
      )}

      {/* Add form */}
      {showAdd && (
        <AddWebhookForm onAdd={handleAdd} onCancel={() => setShowAdd(false)} />
      )}

      {/* Test status toast */}
      {testStatus && (
        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-[var(--wm-text-ui)] font-medium ${testStatus.status === 'firing' ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400' : testStatus.status === 'ok' ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400' : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400'}`}>
          {testStatus.status === 'firing' && (
            <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          )}
          {testStatus.status === 'firing' ? 'Firing test event…' : testStatus.status === 'ok' ? 'Test event sent successfully.' : 'Test event failed.'}
        </div>
      )}

      {/* Webhook list */}
      {hooks.length === 0 && !showAdd && (
        <div className="text-center py-10 border border-dashed border-gray-200 dark:border-[#243348] rounded-xl">
          <svg className="w-8 h-8 mx-auto text-gray-300 dark:text-[#243348] mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
          </svg>
          <p className="text-[var(--wm-text-ui)] font-medium text-[#64748B] dark:text-[#7D93AE]">No webhooks configured</p>
          <p className="text-[var(--wm-text-caption)] text-[#64748B]/70 dark:text-[#7D93AE]/70 mt-0.5">Click "Add Webhook" to register your first endpoint.</p>
        </div>
      )}

      <div className="space-y-2">
        {hooks.map(hook => (
          <WebhookRow
            key={hook.id}
            hook={hook}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
            onTest={handleTest}
          />
        ))}
      </div>

      {/* Events reference */}
      {hooks.length > 0 && (
        <div className="mt-4 p-3 rounded-lg bg-gray-50 dark:bg-[#0F1923] border border-gray-100 dark:border-[#243348]">
          <p className="text-[var(--wm-text-caption)] font-semibold text-[#64748B] dark:text-[#7D93AE] uppercase tracking-wide mb-1.5">Available Events</p>
          <div className="flex flex-wrap gap-1.5">
            {WEBHOOK_EVENTS.map(evt => (
              <span key={evt} className="px-1.5 py-0.5 rounded font-mono text-[var(--wm-text-caption)] bg-white dark:bg-[#1B2A3E] border border-gray-200 dark:border-[#243348] text-[#64748B] dark:text-[#7D93AE]">
                {evt}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
